import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Invitation } from "../models/Invitation";
import { InvitationService } from "../services/InvitationService";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";

interface InvitationContextProps {
  invitation: Invitation | null;
  invitations: Invitation[];
  createInvitation: (
    invitationData: Omit<
      Invitation,
      "_id" | "createdAt" | "updatedAt" | "createdById" | "matterId" | "status"
    >,
  ) => Promise<string>;
  updateInvitation: (
    invitationId: string,
    invitationData: Partial<
      Omit<Invitation, "_id" | "createdAt" | "updatedAt">
    >,
  ) => Promise<void>;
  deleteInvitationById: (invitationId: string) => void;
  setSelectedInvitation: (invitationId: string) => void;
}

const InvitationContext = createContext<InvitationContextProps | undefined>(
  undefined,
);

export const useInvitation = (): InvitationContextProps => {
  const context = useContext(InvitationContext);
  if (!context) {
    throw new Error("useInvitation must be used within a InvitationProvider");
  }
  return context;
};

export const InvitationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();

  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchInvitations = async () => {
    if (!user) return;
    try {
      const res = await InvitationService.getInvitations();
      setInvitations(res);
      setError(null);
    } catch (err: any) {
      console.error("Failed to fetch invitations:", err);
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, [user]);

  useSocketEventByIds("invitations:changed", ["*"], () => {
    fetchInvitations();
  });

  const setSelectedInvitation = (invitationId: string) => {
    const selectedInvitation =
      invitations.find((f) => f._id === invitationId) || null;
    setInvitation(selectedInvitation);
  };

  const createInvitation = async (
    invitationData: Omit<
      Invitation,
      "_id" | "createdAt" | "updatedAt" | "createdById" | "matterId" | "status"
    >,
  ): Promise<string> => {
    if (!currentMatterId) return "";
    const newInvitationId = await InvitationService.createInvitation({
      ...invitationData,
      matterId: currentMatterId,
    });
    return newInvitationId;
  };

  const updateInvitation = async (
    invitationId: string,
    updatedData: Partial<Omit<Invitation, "_id" | "createdAt" | "updatedAt">>,
  ) => {
    await InvitationService.updateInvitation(invitationId, updatedData);
  };

  const deleteInvitationById = async (invitationId: string) => {
    if (!currentMatterId) return;
    await InvitationService.deleteInvitation(invitationId);
    fetchInvitations();
  };

  return (
    <InvitationContext.Provider
      value={{
        invitation,
        invitations,
        createInvitation,
        updateInvitation,
        deleteInvitationById,
        setSelectedInvitation,
      }}
    >
      {children}
    </InvitationContext.Provider>
  );
};
